import axios from 'axios';

import { InfraStrukturDB } from 'boot/queryStoreAndCommandBus';
import { IDBPDatabase } from 'idb';
import ReconnectingWebSocket from 'reconnecting-websocket';
import Stomp from 'webstomp-client';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';

import { INSTANCE_AUTHENTICATION_CONFIGURATION_KEY } from 'src/services/authentication';

import { EventBus } from 'src/util/eventBus/EventBus';
import { MessageBus } from 'src/util/messagebus/MessageBus';
import { QueryBuilder } from 'src/util/query/QueryBuilder';
import { QueryStore } from 'src/util/query/QueryStore';
import { IFSSocket } from 'src/util/socket/IFSSocket';
import { TopicFactory } from 'src/util/socket/TopicFactory';
import { TopicSubscriptions } from 'src/util/socket/TopicSubscriptions';
import { PongConfiguration } from 'src/util/socket/types/PongConfiguration';

class QueryStoreFactory {
	private readonly eventBus: EventBus;
	private readonly ifsSocket: IFSSocket;
	private readonly indexedDB: IDBPDatabase<InfraStrukturDB> | null;
	private readonly instanceName: string;
	private readonly instanceUrl: string;
	private readonly messageBus: MessageBus;
	private readonly pongConfiguration: PongConfiguration;
	private readonly queryBuilder: QueryBuilder;
	private readonly socket: ReconnectingWebSocket;
	private readonly topicFactory: TopicFactory;
	private readonly topicSubscriptions: TopicSubscriptions;

	constructor(
		instanceName: string,
		instanceUrl: string,
		pongConfiguration: PongConfiguration,
		indexedDB: IDBPDatabase<InfraStrukturDB> | null,
		eventBus: EventBus
	) {
		this.instanceName = instanceName;
		this.instanceUrl = instanceUrl;
		this.pongConfiguration = pongConfiguration;
		this.indexedDB = indexedDB;
		this.topicFactory = new TopicFactory(this.pongConfiguration);
		this.queryBuilder = new QueryBuilder(this.instanceUrl);
		this.socket = new ReconnectingWebSocket(
			`${this.pongConfiguration.pongHost}:${this.pongConfiguration.pongPort}/ws`
		);
		this.topicSubscriptions = new TopicSubscriptions();
		this.ifsSocket = new IFSSocket(
			this.socket,
			Stomp,
			this.pongConfiguration,
			this.topicFactory,
			this.topicSubscriptions
		);
		this.messageBus = new MessageBus(this.ifsSocket);
		this.eventBus = eventBus;
	}

	public getMessageBus(): MessageBus {
		return this.messageBus;
	}

	public create() {
		const allInstanceAuthenticationConfigurationsData = JSON.parse(
			localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) as string
		) as Array<InstanceAuthenticationConfiguration>;
		const instanceAuthenticationConfiguration = allInstanceAuthenticationConfigurationsData.find(
			(instanceConfiguration) => instanceConfiguration.name === this.instanceName
		);
		if (instanceAuthenticationConfiguration !== undefined) {
			return new QueryStore(
				this.queryBuilder,
				this.messageBus,
				this.eventBus,
				axios,
				this.indexedDB,
				this.instanceName,
				instanceAuthenticationConfiguration.user as string,
				process.env.VERSION
			);
		} else {
			throw new Error('queryStore can not function without authentication');
		}
	}

	public dispose(): void {
		this.messageBus.dispose();
	}
}

export { QueryStoreFactory };
